//@flow

import _ from 'lodash';
// app
import type { Observable, ObservableArray } from 'rgs-knockout';
import ko from 'rgs-knockout';
import type { ComponentInfo } from 'rgs-knockout/types';

// rgs-nfd-ui-kit
import { withProps, BaseComponent } from './../../../helpers';
import type {
	TDropdownPrimary,
	TDropdownParts,
	TDropdownRest,
	TOuterDropdownParts,
} from './contracts';

const LIST_EMPTY = 'Ничего не найдено';

export type TDropdownHandlers = {
	onMapItemValue: _.noop,
	onInputChange: _.noop,
	onItemClick: _.noop,
	onFilterBy: _.noop,
}
export type TDropdownWithTypeaheadOptions = {
	isOpened: Observable<boolean>,
	primary: TDropdownPrimary,
	parts: TDropdownParts,
	handlers: TDropdownHandlers,
	rest: TDropdownRest,
}

export const withTypeahead = (superClass: any) => (
	class DropdownWithTypeahead extends superClass {
		handlers: any; //TODO: TYPES
		primary: TDropdownPrimary;
		parts: TDropdownParts;
		rest: TDropdownRest;

		props: {
			isOpened?: Observable<boolean>; //may be inherited

			handlers: any;
			primary: TDropdownPrimary;
			parts: TDropdownParts;
			rest: TDropdownRest
		};

		constructor (options: { props: TDropdownWithTypeaheadOptions }, ref: ComponentInfo) {
			super(options, ref);

			const {
				handlers,
				primary,
				parts,
				rest,
			} = withProps(options);

			this.handlers = this.assignHandlers(handlers);
			this.primary = this.createPrimary(primary);
			this.parts = this.createParts(parts);

			this.rest = this.assignRestMoments(rest);

			this.props = {
				...(this.props || {}), //inherited
				primary: { ...this.primary },
				handlers: { ...this.handlers },
				parts: { ...this.parts },
				rest: { ...this.rest },
			};
		}

		get inputValue (): any {
			const value = this.primary.inputValue || { peek: _.noop };
			return value.peek();
		}

		assignHandlers (handlers: any) {
			const {
				onItemClick,
				onInputChange,
				onTypeaheadToggle,
				onFilterBy,
				onMapItemValue,
			} = handlers || {};

			return {
				onItemClick: this.handleClickByItem.bind(null, onItemClick),
				onInputChange: this.handleInputChange.bind(null, onInputChange),
				onMapItemValue: onMapItemValue,
				onTypeaheadToggle: onTypeaheadToggle || _.noop,
				onFilterBy: onFilterBy || useDefault(onMapItemValue), //will be filter items on ui
			};
		}

		assignRestMoments (rest: TDropdownRest) {
			const { inputAttrs } = rest || {};

			return {
				inputAttrs,
				inputWidth: ko.pureComputed(() =>
					ko.unwrap(this.parts.label.text) === '' ? '100%' : '70%',
				),
			};
		}

		createPrimary (primary: TDropdownPrimary) {
			const { inputValue, items, isFetching = false } = primary || {};

			const value = inputValue || ko.observable();
			value.subscribe(this.handlers.onInputChange);

			return {
				isFetching: ko.pureComputed(() => ko.unwrap(isFetching)),
				inputValue: ko.pureComputed({
					read: () => value(),
					write: update => {
						const source = _.isObject(update)
							? this.handlers.onMapItemValue(update) : update;

						value(source);
					},
				}),
				tempInputValue: primary.tempInputValue || ko.observable(),
				items: ko.pureComputed(() => this.mapItemsToUI(items)),
			};
		}

		createParts (parts: TOuterDropdownParts): TDropdownParts {
			// eslint-disable-next-line max-len
			const { onMoreButton, onList, onSearchLabel, onEmpty, onListItem, onLoupeIcon } = parts || {};

			const { value: searchLabelValue, onIconCloseClick } = onSearchLabel || {};

			return {
				listItem: new BaseComponent({
					...onListItem,
				}),
				btnMore: new BaseComponent({
					...onMoreButton,
				}),
				list: new BaseComponent({
					visible: ko.pureComputed(() => this.primary.items().length === 0),
					...onList,
				}),
				label: new BaseComponent({
					visible: ko.pureComputed(() => ko.unwrap(searchLabelValue)),
					onClick: onIconCloseClick,
					text: searchLabelValue,
				}),
				empty: {
					text: LIST_EMPTY,
					...onEmpty,
				},
				loupeIcon: {
					...onLoupeIcon,
				},
			};
		}

		//method will call from parent (subscribe on isOpened)
		//use only on <nfd-typeahead>
		handleDropdownToggle (isOpened: boolean) {
			this.handlers.onTypeaheadToggle(isOpened, this.inputValue);
		}

		//note: don't forget to cast to same word registers 'uppercase/lower....'
		mapItemsToUI (items: ObservableArray<any> | Array<any>): any {
			const normalizedItems: Array<any> = ko.unwrap(items) || [];

			return normalizedItems.filter(this.handlers.onMapItemValue);
		}

		handleInputChange = (callback: (string) => void, inputValue: any) => {
			const isOpened = this.props.isOpened;//if inherited

			if (callback) {
				callback(inputValue);
			}

			if (isOpened && !isOpened.peek()) {
				isOpened(true);
			}
		};

		handleClickByItem = (callback: (any, string) => void, item: any) => {
			if (callback) {
				callback(item, this.inputValue);
			}

			return true; //must be
		};
	}
);

function useDefault (mapper) {
	return (item: any, inputValue: string) => {
		const mappedValue = (mapper(item) || '').toUpperCase();

		return mappedValue.includes(inputValue.toUpperCase());
	};
}

export class DropdownTypeahead extends withTypeahead(class {}) {

}

